import {
  canonicalNumber,
  canonicalAngleDegrees,
  canonicalRotation,
  canonicalValue,
} from './canonical-json.js';

export {
  canonicalNumber,
  canonicalAngleDegrees,
  canonicalRotation,
  canonicalValue,
} from './canonical-json.js';

/**
 * The single registration table for persistent primitive types. Adding an
 * add-command that must survive reload requires one entry here; unsupported
 * runtime objects fail closed instead of silently disappearing from a save.
 */
export const PRIMITIVE_CREATION_COMMANDS = Object.freeze({
  box: 'addBox',
  sphere: 'addSphere',
  cylinder: 'addCylinder',
  mesh: 'addMesh',
});

function exactTransform(object, described) {
  if (!object?.position?.toArray || !object?.rotation || !object?.scale?.toArray) {
    return {
      position: described.position,
      rotation: canonicalRotation(described.rotationDeg),
      scale: described.scale,
      parent: described.parent,
    };
  }
  return {
    position: object.position.toArray(),
    rotation: canonicalRotation(
      [object.rotation.x, object.rotation.y, object.rotation.z]
        .map((radians) => radians * 180 / Math.PI),
    ),
    scale: object.scale.toArray(),
    parent: object.parent?.userData?.anvilId ?? null,
  };
}

function creationEntry(registry, object) {
  const described = registry.describe(object);
  const transform = exactTransform(object, described);
  const common = {
    id: described.id,
    name: object?.name ?? described.name,
    parent: transform.parent,
    position: transform.position,
    rotation: transform.rotation,
    scale: transform.scale,
  };
  if (described.type === 'group') {
    return { cmd: 'group', args: canonicalValue({ ...common, ids: [] }), id: described.id };
  }
  const cmd = PRIMITIVE_CREATION_COMMANDS[described.type];
  if (!cmd) throw new Error(`unsupported Anvil object type '${described.type}'`);
  return {
    cmd,
    args: canonicalValue({
      ...described.params,
      ...common,
      color: described.color,
      roughness: described.roughness,
      metalness: described.metalness,
    }),
    id: described.id,
  };
}

export function serializeObject(registry, object) {
  const entries = [creationEntry(registry, object)];
  for (const child of object.children ?? []) {
    if (child.userData?.anvilId) entries.push(...serializeObject(registry, child));
  }
  return entries;
}

export function serializeScene(registry) {
  const seen = new Set();
  const entries = [];
  for (const described of registry.list()) {
    if (seen.has(described.id)) {
      throw new Error(`duplicate anvilId '${described.id}'`);
    }
    seen.add(described.id);
    if (described.parent == null) entries.push(...serializeObject(registry, registry.require(described.id)));
  }
  return entries;
}
